import { useEffect, useState } from 'react';
import { ChevronRight, Home, Play } from 'lucide-react';
import { useAuth } from './AuthModule';
import { useT } from '../i18n/I18nModule';

const API = process.env.REACT_APP_API_URL || '';

const FINNISH_MONTHS = [
  'tammikuuta',
  'helmikuuta',
  'maaliskuuta',
  'huhtikuuta',
  'toukokuuta',
  'kesäkuuta',
  'heinäkuuta',
  'elokuuta',
  'syyskuuta',
  'lokakuuta',
  'marraskuuta',
  'joulukuuta',
];

function getEventStartTime(ev) {
  const [hh, mm = 0] = (ev.time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  if (ev.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate)) {
    const [y, m, day] = ev.eventDate.split('-').map((x) => parseInt(x, 10));
    d.setFullYear(y, m - 1, day);
  }
  d.setHours(hh, mm, 0, 0);
  return d;
}

function getEventEndTime(ev) {
  const start = getEventStartTime(ev);
  const durationMinutes = ev.minutes != null ? Math.max(ev.minutes, 60) : 60;
  return new Date(start.getTime() + durationMinutes * 60 * 1000);
}

function formatClockLabel(now) {
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const day = now.getDate();
  const month = FINNISH_MONTHS[now.getMonth()];
  return `${hh}:${mm} ${day}. ${month}`;
}

function formatTimeUntil(startsAt, now = new Date()) {
  const diffMs = startsAt.getTime() - now.getTime();
  const totalMinutes = Math.round(diffMs / 60000);
  const absM = Math.abs(totalMinutes);
  const hours = Math.floor(absM / 60);
  const minutes = absM % 60;
  const parts = [];
  if (hours) parts.push(`${hours} h`);
  if (minutes || parts.length === 0) parts.push(`${minutes} min`);
  const str = parts.join(' ');
  return totalMinutes < 0 ? `${str} sitten` : str;
}

/**
 * Top bar of the chat view: close button (fixed top-right), event countdown.
 * Shows a join button when there is a currently live event (same logic as DashboardPage),
 * and under it text about when the next event starts.
 */
function ChatTopBar({ onBack, nowLiveEvent, closestEvent, nextEvent }) {
  const tLabel = useT();
  const [now, setNow] = useState(() => new Date());
  const { user } = useAuth();

  useEffect(() => {
    if (!closestEvent && !nextEvent) return;
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [closestEvent, nextEvent]);

  // Event we consider "next up" for countdown/join: only use real upcoming nextEvent
  const primaryEvent = nextEvent || null;
  const startsAt = primaryEvent?.startsAt instanceof Date
    ? primaryEvent.startsAt
    : primaryEvent
    ? getEventStartTime(primaryEvent)
    : null;
  const timeUntil = startsAt ? formatTimeUntil(startsAt, now) : '';

  const minutesUntilStart =
    startsAt != null ? Math.ceil((startsAt.getTime() - now.getTime()) / 60000) : null;
  const canPreJoin =
    primaryEvent &&
    primaryEvent.id &&
    minutesUntilStart != null &&
    minutesUntilStart >= 0 &&
    minutesUntilStart <= 10;

  // Live join button follows the same "now live" logic as DashboardPage
  const liveEvent = nowLiveEvent && nowLiveEvent.id ? nowLiveEvent : null;

  const liveEndsAt = liveEvent ? getEventEndTime(liveEvent) : null;
  const minutesUntilEnd =
    liveEndsAt != null ? Math.ceil((liveEndsAt.getTime() - now.getTime()) / 60000) : null;
  const isEndingSoon =
    liveEvent && minutesUntilEnd != null && minutesUntilEnd >= 0 && minutesUntilEnd <= 10;

  const time = `${String(now.getHours()).padStart(2, '0')}:${String(
    now.getMinutes(),
  ).padStart(2, '0')}`;
  const clockLabel = formatClockLabel(now);

  return (
    <div className="Chat-event-countdown-wrap">
      <a
        href="/"
        className="menu-clock Chat-top-clock"
        aria-label={clockLabel}
        title={clockLabel}
      >
        <Home className="menu-clock-home-icon" size={18} aria-hidden />
        <span>{time}</span>
      </a>
      {liveEvent ? (
        isEndingSoon ? (
          <div
            className="Button live-btn"
            aria-disabled="true"
            title="Ei voi osallistua enää."
          >
            &quot;{liveEvent.title || '—'}&quot; {tLabel('event.endsSoon')}
          </div>
        ) : (
          <button
            type="button"
            className="Button live-btn"
            onClick={() => {
              if (!liveEvent?.id) return;
              const payload = user?.id ? { action: 'login', userId: user.id } : { action: 'login' };
              fetch(`${API}/api/events/${encodeURIComponent(liveEvent.id)}/logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              }).catch(() => {});
              window.location.href = `/event/stream/${liveEvent.id}`;
            }}
          >
            <Play className="Dashboard-live-btn-icon" /> &quot;
             LIITY TAPAHTUMAAN &quot;{liveEvent?.title || '—'}&quot; NYT
          </button>
        )
      ) : null}

      {primaryEvent ? (
        <>
          <br />
          {canPreJoin ? (
            <button
              type="button"
              className="Button event-info event-info-button"
              onClick={() =>
                primaryEvent?.id &&
                (window.location.href = `/event/stream/${primaryEvent.id}`)
              }
            >
              Tapahtuma "{primaryEvent.title}" alkaa {timeUntil} päästä
            </button>
          ) : (
            <div className="event-info">
              Tapahtuma "{primaryEvent.title}" alkaa {timeUntil} päästä
            </div>
          )}
        </>
      ) : !nextEvent ? (
        <div className="event-info event-info-none">Ei tulevia tapahtumia.</div>
      ) : null}

      <button
        type="button"
        className="Chat-close"
        onClick={onBack}
        aria-label="Sulje chat"
        title="Sulje chat"
      >
        <ChevronRight className="Chat-close-icon" size={24} strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default ChatTopBar;
